/**
 * PublicFeedback Page (US-096)
 *
 * Standalone public page where recruiters can rate AI review accuracy
 * without logging in. Accessed via /feedback/:token
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ArbieMascot } from '../components/ArbieMascot';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { Alert } from '../components/ui/Alert';

const BASE_URL = import.meta.env.VITE_API_URL || '';

// Character limits — must match INPUT_LIMITS in public-feedback.ts
const LIMITS = {
  comments: 10000,
  reviewGuidance: 10000,
  scoreAssessment: 1000,
} as const;

type SummarizableField = keyof typeof LIMITS;

// -------------------------------------------------------------------
// CharCounter component
// -------------------------------------------------------------------

interface CharCounterProps {
  value: string;
  limit: number;
  field: SummarizableField;
  token: string | undefined;
  onSummarized: (text: string) => void;
}

const CharCounter: React.FC<CharCounterProps> = ({ value, limit, field, token, onSummarized }) => {
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summarizeError, setSummarizeError] = useState<string | null>(null);

  const len = value.length;
  const isOver = len > limit;
  const pct = len / limit;

  const colorClass = isOver
    ? 'text-red-600 dark:text-red-400'
    : pct >= 0.8
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-gray-400 dark:text-gray-500';

  const handleSummarize = async () => {
    if (!token) return;
    setIsSummarizing(true);
    setSummarizeError(null);
    try {
      const res = await fetch(`${BASE_URL}/api/feedback/token/${token}/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field, text: value }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Summarize failed');
      onSummarized(data.summarized);
    } catch (err) {
      setSummarizeError(err instanceof Error ? err.message : 'Summarize failed');
    } finally {
      setIsSummarizing(false);
    }
  };

  return (
    <div className="mt-1.5 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {isOver && (
          <button
            type="button"
            onClick={handleSummarize}
            disabled={isSummarizing}
            className="text-xs px-2 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 disabled:opacity-50 transition-colors"
          >
            {isSummarizing ? 'Summarizing…' : 'Summarize to fit'}
          </button>
        )}
        {summarizeError && (
          <span className="text-xs text-red-500 dark:text-red-400">{summarizeError}</span>
        )}
      </div>
      <span className={`text-xs tabular-nums ${colorClass}`}>
        {len.toLocaleString()} / {limit.toLocaleString()}
      </span>
    </div>
  );
};

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

interface PreviousFeedbackEntry {
  id: number;
  condensedContent: string;
  rawContent: {
    accuracy?: string;
    comments?: string;
    issues?: string[];
    requestReview?: boolean;
    submittedAt?: string;
  } | null;
  createdAt: string;
  submitterId: string | null;
}

interface ReviewData {
  reviewId: number;
  candidateName: string;
  jobTitle: string;
  recommendation: number;
  overallScore: number;
  summary: string;
  strengths: string | null;
  concerns: string | null;
  previousFeedback: PreviousFeedbackEntry[];
}

type Accuracy = 'accurate' | 'partial' | 'inaccurate';
type ScoreAssessment = 'too_high' | 'about_right' | 'too_low';
type PageState = 'loading' | 'form' | 'submitted' | 'expired' | 'not_found';

const ISSUE_OPTIONS = [
  { value: 'missed_key_skills', label: 'Missed key skills' },
  { value: 'wrong_experience', label: 'Wrong experience assessment' },
  { value: 'score_mismatch', label: "Score doesn't match quality" },
  { value: 'missing_context', label: 'Missing context' },
  { value: 'screening_conflict', label: 'Screening conflict' },
  { value: 'other', label: 'Other' },
];

// -------------------------------------------------------------------
// Recommendation label/color helpers
// -------------------------------------------------------------------

function getRecommendationLabel(level: number): string {
  switch (level) {
    case 1: return 'Not Recommended';
    case 2: return 'Weak';
    case 3: return 'Moderate';
    case 4: return 'Recommended';
    case 5: return 'Strongly Recommended';
    default: return 'Unknown';
  }
}

function getRecommendationClasses(level: number): string {
  switch (level) {
    case 1: return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 2: return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case 3: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 4: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 5: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

function getScoreColor(score: number): string {
  const pct = score * 2; // overallScore is 0-50 (score5 * 10), so * 2 = 0-100%
  if (pct >= 70) return 'text-green-600 dark:text-green-400';
  if (pct >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// -------------------------------------------------------------------
// Component
// -------------------------------------------------------------------

const PublicFeedback: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  // Page state
  const [pageState, setPageState] = useState<PageState>('loading');
  const [review, setReview] = useState<ReviewData | null>(null);
  const [_errorMsg, _setErrorMsg] = useState<string>('');

  // Pre-select accuracy from quick-rate email link (?rating=accurate|partial|inaccurate)
  const ratingParam = searchParams.get('rating');
  const initialAccuracy = ratingParam && ['accurate', 'partial', 'inaccurate'].includes(ratingParam)
    ? (ratingParam as Accuracy) : null;

  // Form state
  const [accuracy, setAccuracy] = useState<Accuracy | null>(initialAccuracy);
  const [scoreAssessment, setScoreAssessment] = useState<ScoreAssessment | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [comments, setComments] = useState('');
  const [requestReview, setRequestReview] = useState(false);
  const [reviewGuidance, setReviewGuidance] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch review data
  useEffect(() => {
    if (!token) {
      setPageState('not_found');
      return;
    }

    async function fetchReview() {
      try {
        const response = await fetch(`${BASE_URL}/api/feedback/token/${token}`);

        if (response.status === 404) {
          setPageState('not_found');
          return;
        }
        if (response.status === 410) {
          setPageState('expired');
          return;
        }
        if (!response.ok) {
          setPageState('not_found');
          return;
        }

        const data = await response.json();
        setReview(data);
        setPageState('form');
      } catch {
        setPageState('not_found');
      }
    }

    fetchReview();
  }, [token]);

  // Toggle issue checkbox
  const toggleIssue = useCallback((value: string) => {
    setIssues((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value],
    );
  }, []);

  // Submit feedback
  const handleSubmit = useCallback(async () => {
    if (!accuracy || !token) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const body: Record<string, unknown> = { accuracy };
      if (scoreAssessment) body.scoreAssessment = scoreAssessment;
      if (issues.length > 0) body.issues = issues;
      if (comments.trim()) body.comments = comments.trim();
      if (requestReview) {
        body.requestReview = true;
        if (reviewGuidance.trim()) body.reviewGuidance = reviewGuidance.trim();
      }
      if (name.trim()) body.name = name.trim();
      if (email.trim()) body.email = email.trim();

      const response = await fetch(`${BASE_URL}/api/feedback/token/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.status === 410) {
        setPageState('expired');
        return;
      }
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Failed to submit feedback' }));
        throw new Error(err.error || 'Failed to submit feedback');
      }

      setPageState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  }, [accuracy, scoreAssessment, issues, comments, requestReview, reviewGuidance, name, email, token]);

  // Whether to show "what was wrong" and score assessment sections
  const showDetailedFeedback = accuracy === 'partial' || accuracy === 'inaccurate';

  // Disable submit if any field is over its limit
  const isOverLimit =
    comments.length > LIMITS.comments ||
    reviewGuidance.length > LIMITS.reviewGuidance;

  // -------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------

  const renderHeader = () => (
    <div className="flex items-center justify-center gap-3 py-6">
      <ArbieMascot size="md" />
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Recruiter Buddy</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">AI Review Feedback</p>
      </div>
    </div>
  );

  const renderLoading = () => (
    <Card variant="elevated">
      <CardBody className="py-8">
        <div className="space-y-4">
          <Skeleton variant="text" width="60%" height="24px" />
          <Skeleton variant="text" width="40%" height="18px" />
          <Skeleton variant="rectangle" width="100%" height="120px" />
          <Skeleton variant="text" lines={3} />
          <Skeleton variant="rectangle" width="100%" height="48px" />
        </div>
      </CardBody>
    </Card>
  );

  const renderNotFound = () => (
    <Card variant="elevated">
      <CardBody className="py-12 text-center">
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Invalid Feedback Link</h2>
        <p className="text-gray-500 dark:text-gray-400">
          This feedback link is not valid. It may have been mistyped or the review may no longer exist.
        </p>
      </CardBody>
    </Card>
  );

  const renderExpired = () => (
    <Card variant="elevated">
      <CardBody className="py-12 text-center">
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-amber-500 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Feedback Link Expired</h2>
        <p className="text-gray-500 dark:text-gray-400">
          This feedback link has expired. Please request a new link from the hiring team.
        </p>
      </CardBody>
    </Card>
  );

  const handleSubmitAnother = () => {
    setAccuracy(null);
    setScoreAssessment(null);
    setIssues([]);
    setComments('');
    setRequestReview(false);
    setReviewGuidance('');
    setName('');
    setEmail('');
    setSubmitError(null);
    setPageState('form');
  };

  const renderSubmitted = () => (
    <Card variant="elevated">
      <CardBody className="py-12 text-center">
        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
          <svg className="w-8 h-8 text-green-500 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Thank You!</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Your feedback has been submitted successfully. It will help improve future AI reviews.
        </p>
        {requestReview && (
          <Alert variant="info" className="text-left mt-4">
            An updated review has been requested. The hiring team will be notified.
          </Alert>
        )}
        <Button
          variant="secondary"
          size="md"
          onClick={handleSubmitAnother}
          className="mt-4"
        >
          Submit Another Feedback
        </Button>
      </CardBody>
    </Card>
  );

  const renderReviewSummary = () => {
    if (!review) return null;

    // Split strengths and concerns by newlines or bullet points
    const strengthsList = review.strengths
      ? review.strengths.split(/\n|(?:^|\n)\s*[-*]\s*/g).filter((s) => s.trim())
      : [];
    const concernsList = review.concerns
      ? review.concerns.split(/\n|(?:^|\n)\s*[-*]\s*/g).filter((s) => s.trim())
      : [];

    return (
      <Card variant="bordered" className="mb-6">
        <CardHeader borderBottom>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {review.candidateName}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{review.jobTitle}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium ${getRecommendationClasses(review.recommendation)}`}>
                {getRecommendationLabel(review.recommendation)}
              </span>
              <span className={`text-2xl font-bold ${getScoreColor(review.overallScore)}`}>
                {review.overallScore * 2}<span className="text-sm font-normal text-gray-400 dark:text-gray-500">%</span>
              </span>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Summary */}
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{review.summary}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Strengths */}
            {strengthsList.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-green-600 dark:text-green-400 mb-2">
                  Strengths
                </h4>
                <ul className="space-y-1">
                  {strengthsList.map((s, i) => (
                    <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-1.5">
                      <svg className="w-4 h-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {s.trim()}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Concerns */}
            {concernsList.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">
                  Concerns
                </h4>
                <ul className="space-y-1">
                  {concernsList.map((c, i) => (
                    <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-1.5">
                      <svg className="w-4 h-4 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      {c.trim()}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderPreviousFeedback = () => {
    if (!review?.previousFeedback || review.previousFeedback.length === 0) return null;

    return (
      <Card variant="bordered" className="mb-6">
        <CardBody>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Previous Feedback ({review.previousFeedback.length})
          </h3>
          <div className="space-y-2">
            {review.previousFeedback.map((entry) => {
              const raw = entry.rawContent;
              const accuracyLabel = raw?.accuracy
                ? { accurate: 'Accurate', partial: 'Partially Accurate', inaccurate: 'Inaccurate' }[raw.accuracy] ?? raw.accuracy
                : null;
              const accuracyColor = raw?.accuracy
                ? { accurate: 'text-green-600 dark:text-green-400', partial: 'text-amber-600 dark:text-amber-400', inaccurate: 'text-red-600 dark:text-red-400' }[raw.accuracy] ?? ''
                : '';

              return (
                <div
                  key={entry.id}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2"
                >
                  <div className="flex items-center justify-between text-xs mb-1">
                    {accuracyLabel && (
                      <span className={`font-medium ${accuracyColor}`}>{accuracyLabel}</span>
                    )}
                    <span className="text-gray-400 dark:text-gray-500">
                      {new Date(entry.createdAt).toLocaleDateString()}
                      {entry.submitterId && entry.submitterId !== 'anonymous' && (
                        <> &middot; {entry.submitterId}</>
                      )}
                    </span>
                  </div>
                  {raw?.comments && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">{raw.comments}</p>
                  )}
                  {raw?.issues && raw.issues.length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                      Issues: {raw.issues.join(', ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderForm = () => (
    <div className="space-y-6">
      {/* Review Summary */}
      {renderReviewSummary()}

      {/* Previous Feedback */}
      {renderPreviousFeedback()}

      {/* Error */}
      {submitError && (
        <Alert variant="error" dismissible onDismiss={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {/* Overall Accuracy */}
      <Card variant="elevated">
        <CardBody>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            How accurate was this AI review? <span className="text-red-500">*</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {([
              { value: 'accurate', label: 'Accurate', desc: 'Review matches my assessment', color: 'green' },
              { value: 'partial', label: 'Partially Accurate', desc: 'Some parts were off', color: 'amber' },
              { value: 'inaccurate', label: 'Inaccurate', desc: 'Significantly wrong', color: 'red' },
            ] as const).map((opt) => {
              const isSelected = accuracy === opt.value;
              const colorMap = {
                green: {
                  selected: 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-400',
                  ring: 'ring-green-500 dark:ring-green-400',
                  dot: 'bg-green-500 dark:bg-green-400',
                  label: 'text-green-700 dark:text-green-300',
                },
                amber: {
                  selected: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-400',
                  ring: 'ring-amber-500 dark:ring-amber-400',
                  dot: 'bg-amber-500 dark:bg-amber-400',
                  label: 'text-amber-700 dark:text-amber-300',
                },
                red: {
                  selected: 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-400',
                  ring: 'ring-red-500 dark:ring-red-400',
                  dot: 'bg-red-500 dark:bg-red-400',
                  label: 'text-red-700 dark:text-red-300',
                },
              };
              const colors = colorMap[opt.color];

              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setAccuracy(opt.value);
                    // Reset dependent fields when switching to "accurate"
                    if (opt.value === 'accurate') {
                      setScoreAssessment(null);
                      setIssues([]);
                      setRequestReview(false);
                      setReviewGuidance('');
                    }
                  }}
                  className={`
                    relative p-4 rounded-lg border-2 text-left transition-all
                    ${isSelected
                      ? `${colors.selected} ring-2 ${colors.ring}`
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                    }
                  `}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-3 h-3 rounded-full ${isSelected ? colors.dot : 'bg-gray-300 dark:bg-gray-600'}`} />
                    <span className={`text-sm font-medium ${isSelected ? colors.label : 'text-gray-900 dark:text-gray-100'}`}>
                      {opt.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 ml-5">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* Score Assessment (shown if not "Accurate") */}
      {showDetailedFeedback && (
        <Card variant="elevated">
          <CardBody>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              How was the score?
            </h3>
            <div className="flex flex-wrap gap-3">
              {([
                { value: 'too_high', label: 'Too High' },
                { value: 'about_right', label: 'About Right' },
                { value: 'too_low', label: 'Too Low' },
              ] as const).map((opt) => {
                const isSelected = scoreAssessment === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setScoreAssessment(isSelected ? null : opt.value)}
                    className={`
                      px-4 py-2 rounded-lg border text-sm font-medium transition-all
                      ${isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-400 ring-2 ring-primary-500 dark:ring-primary-400'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }
                    `}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* What was wrong? (shown if not "Accurate") */}
      {showDetailedFeedback && (
        <Card variant="elevated">
          <CardBody>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              What was wrong?
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ISSUE_OPTIONS.map((opt) => {
                const isChecked = issues.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className={`
                      flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer transition-all
                      ${isChecked
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleIssue(opt.value)}
                      className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Comments */}
      <Card variant="elevated">
        <CardBody>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Additional Comments <span className="text-gray-400 font-normal">(optional)</span>
          </h3>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Any additional feedback about this review..."
            rows={4}
            className={`
              w-full rounded-lg border
              ${comments.length > LIMITS.comments
                ? 'border-red-400 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'}
              bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
              placeholder-gray-400 dark:placeholder-gray-500
              px-3 py-2 text-sm
              focus:outline-none focus:ring-2
              resize-vertical
            `}
          />
          <CharCounter
            value={comments}
            limit={LIMITS.comments}
            field="comments"
            token={token}
            onSummarized={setComments}
          />
        </CardBody>
      </Card>

      {/* Request Updated Review (shown if "Inaccurate") */}
      {accuracy === 'inaccurate' && (
        <Card variant="elevated">
          <CardBody>
            <label className="flex items-center gap-2.5 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={requestReview}
                onChange={(e) => setRequestReview(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Request an updated review
              </span>
            </label>
            {requestReview && (
              <>
                <textarea
                  value={reviewGuidance}
                  onChange={(e) => setReviewGuidance(e.target.value)}
                  placeholder="What should the updated review focus on or correct?"
                  rows={4}
                  className={`
                    w-full rounded-lg border
                    ${reviewGuidance.length > LIMITS.reviewGuidance
                      ? 'border-red-400 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500 focus:border-primary-500'}
                    bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                    placeholder-gray-400 dark:placeholder-gray-500
                    px-3 py-2 text-sm
                    focus:outline-none focus:ring-2
                    resize-vertical
                  `}
                />
                <CharCounter
                  value={reviewGuidance}
                  limit={LIMITS.reviewGuidance}
                  field="reviewGuidance"
                  token={token}
                  onSummarized={setReviewGuidance}
                />
              </>
            )}
          </CardBody>
        </Card>
      )}

      {/* Name & Email (optional) */}
      <Card variant="elevated">
        <CardBody>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Your Info <span className="text-gray-400 font-normal">(optional)</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="
                  w-full rounded-lg border border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                  placeholder-gray-400 dark:placeholder-gray-500
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                "
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="
                  w-full rounded-lg border border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                  placeholder-gray-400 dark:placeholder-gray-500
                  px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                "
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Submit */}
      <div className="flex flex-col items-end gap-1">
        {isOverLimit && (
          <p className="text-xs text-red-500 dark:text-red-400">
            One or more fields exceed the character limit. Use &ldquo;Summarize to fit&rdquo; or shorten the text.
          </p>
        )}
        <Button
          size="lg"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!accuracy || isOverLimit}
        >
          Submit Feedback
        </Button>
      </div>
    </div>
  );

  // -------------------------------------------------------------------
  // Main layout
  // -------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-12">
        {renderHeader()}

        {pageState === 'loading' && renderLoading()}
        {pageState === 'not_found' && renderNotFound()}
        {pageState === 'expired' && renderExpired()}
        {pageState === 'submitted' && renderSubmitted()}
        {pageState === 'form' && renderForm()}

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-gray-400 dark:text-gray-500">
          Powered by Recruiter Buddy &middot; AI-powered recruitment automation
        </p>
      </div>
    </div>
  );
};

export default PublicFeedback;
